<template>
    <div :key="id" :class="{ 'opacity-50': disabled }" class="d-flex align-items-center justify-content-between border-top p-3" role="menuitem">
        <span class="flex flex-col gap-1">
            <span class="d-flex align-items-center gap-1">
                <slot />
            </span>
            <span v-if="disabled && disabledMessage" class="text-small text-muted" style="max-width: 16rem; white-space: normal">{{
                disabledMessage
            }}</span>
        </span>
        <a
            v-if="!disabled"
            :href="actualRoute"
            class="position-relative d-flex align-items-center justify-content-center"
            @click.prevent="exportItems"
        >
            <spinner v-if="isProcessing" class="position-absolute" />
            <span :style="{ visibility: isProcessing ? 'hidden' : 'visible' }">{{ trans('general.action.generate') }}</span>
        </a>
        <span v-else class="text-small text-muted" style="cursor: not-allowed">{{ trans('general.action.generate') }}</span>
    </div>
</template>
<script setup>
import { computed, ref, useId } from 'vue';
import { route } from 'ziggy-js';
import { $http } from '@/bootstrap.js';
import { eventBus, events } from '@/eventBus.js';
import { useExportsStore } from '@/stores/exports.js';

const id = useId();
const exportsStore = useExportsStore();

const props = defineProps({
    type: String,
    route: String,
    filters: Object,
    nonQueryFilters: Object, // additional filters that are not part of the URL query params eg. asset id
    selection: Object,
    groupKey: String,
    disabled: { type: Boolean, default: false },
    disabledMessage: String,
});

const emits = defineEmits(['done', 'error']);

const isProcessing = ref(false);

const actualRoute = computed(() => {
    return route(props.route, {
        type: props.type,
        filters: { ...props.filters, ...props.nonQueryFilters },
        selection: props.selection,
    });
});

const getTimestamp = () => {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, '0');
    const d = String(now.getDate()).padStart(2, '0');
    const hh = String(now.getHours()).padStart(2, '0');
    const ii = String(now.getMinutes()).padStart(2, '0');
    const ss = String(now.getSeconds()).padStart(2, '0');

    return `${y}${m}${d}_${hh}${ii}${ss}`;
};

const exportItems = async (event) => {
    if (isProcessing.value) {
        event.preventDefault();
        return;
    }

    isProcessing.value = true;
    const timestamp = getTimestamp();

    try {
        const resp = await $http.get(actualRoute.value, {
            params: { timestamp },
        });
        const retryParams = {
            url: actualRoute.value,
            timestamp,
        };
        exportsStore.addExport(resp.data, props.groupKey, retryParams);
    } catch (error) {
        eventBus.$emit(events.presentFlashMessage, {
            type: 'error',
            payload: {
                error: 'Export failed. Please try again later.',
            },
        });
        emits('error', { id: null, error });
        throw error;
    } finally {
        isProcessing.value = false;
    }
};
</script>
